/**
 * How a jurisdiction is named and flagged — one strategy for every surface (MAR-948 wave 3).
 *
 * There were three: `research.vue`'s Spanish-only `COUNTRY_METADATA` / `COUNTRY_NAMES` tables plus a
 * private `getFlagEmoji`, `utils/jurisdictionFlags.ts` (two countries, named in Spanish), and inline
 * `Intl.DisplayNames` in three pages. Names now come from the platform's `Intl.DisplayNames` in the
 * UI locale, so there is no list to translate or to fall behind the corpus; the flag is derived from
 * the code. `tests/unit/legalDisplayGuard.test.ts` bans a name table, a flag literal and an inline
 * `Intl.DisplayNames` anywhere else.
 *
 * Components use `useJurisdictionName()` (`app/composables/`), which binds the locale and the
 * translated "global" label.
 */

/** The public corpus spanning every jurisdiction, spelled `global` by the API and `GLOBAL` by orgs. */
export function isGlobalJurisdiction(code: string | null | undefined): boolean {
  return String(code || '').trim().toUpperCase() === 'GLOBAL'
}

const REGION_CODE = /^[A-Z]{2}$/
// Regional indicator symbol A (U+1F1E6) minus 'A' (65): a flag is its code's two letters shifted
// into that block.
const REGIONAL_INDICATOR_OFFSET = 0x1F1E6 - 65

/**
 * The display name of a jurisdiction in `locale`: a country via `Intl.DisplayNames`, the global
 * corpus as `globalLabel` (the caller's translated string), the upper-cased code for anything the
 * platform cannot name, and '' for nothing.
 */
export function jurisdictionName(
  code: string | null | undefined,
  locale: string,
  globalLabel: string,
): string {
  const upper = String(code || '').trim().toUpperCase()
  if (!upper) return ''
  if (upper === 'GLOBAL') return globalLabel
  if (!REGION_CODE.test(upper)) return upper
  try {
    return new Intl.DisplayNames([locale], { type: 'region' }).of(upper) || upper
  } catch {
    return upper
  }
}

/** The flag emoji for a country code, a globe for the global corpus, '' for anything else. */
export function jurisdictionFlag(code: string | null | undefined): string {
  const upper = String(code || '').trim().toUpperCase()
  if (upper === 'GLOBAL') return '🌐'
  if (!REGION_CODE.test(upper)) return ''
  return String.fromCodePoint(...[...upper].map(c => c.charCodeAt(0) + REGIONAL_INDICATOR_OFFSET))
}
